import { PaymentService, Route } from 'config';
import { get } from 'utils/network';

import type { State } from 'models/Address';
import Address from 'models/Address';

import type { BraintreeError, Client } from 'braintree-web';
import braintree from 'braintree-web';
import type { ApplePayPaymentRequest } from 'braintree-web/apple-pay';

type CreditCard = {
  number: string;
  cvv: string;
  expirationYear: string;
  expirationMonth: string;
  cardType?: string;
  lastFour?: string;
  lastTwo?: string;
  billingAddress?: Record<string, string>;
};

type TransactionInfo = {
  currencyCode: string;
  totalPriceStatus: string;
  totalPrice: string;
  countryCode?: string;
};

type TokenizedCards = {
  creditCards: {
    details: CreditCard,
    nonce: string,
    type: string,
  }[];
};

type ButtonColorOption = 'gold' | 'blue' | 'silver' | 'black';
type FlowType = 'checkout' | 'vault';
const flow: FlowType = 'vault';

export type ButtonStyle = {
  height?: number;
  color?: ButtonColorOption;
  label?: 'paypal' | 'checkout';
  shape?: 'rect' | 'pill';
  size?: 'small' | 'medium' | 'responsive';
  tagline?: boolean;
};

const style: ButtonStyle = {
  height: 35,
  color: 'gold',
  label: 'paypal',
  shape: 'rect',
  size: 'responsive',
  tagline: false,
};

type GoogleEnv = 'TEST' | 'PRODUCTION';

type PayPalAddress = {
  phone: string;
  firstName: string;
  lastName: string;
  recipientName: string;
  line1: string;
  line2: string;
  city: string;
  state: keyof typeof State;
  postalCode: string;
};

type Token = { token: string; };
type Response = TokenizedCards | BraintreeError;
type OnToken = (paymentService: PaymentService, token: string, address?: Address) => void
type OnAutorize = (token: string, address?: Address) => void

class Braintree {
  static client: Client;

  static async initialize(): Promise<void> {
    const data = await get<Token>(Route.BRAINTREE_CLIENT_CODE);

    Braintree.client = await braintree.client.create({
      authorization: data?.parsedBody?.token || '',
    });
  }

  static async addCreditCard(creditCard: CreditCard): Promise<Response> {
    return Braintree.client.request({
      endpoint: Route.BRAINTREE_CC_CALLBACK,
      method: 'post',
      data: { creditCard },
      // @ts-expect-error TODO: fix null safety
    }, undefined) as unknown as Promise<Response>;
  }

  static async renderPayPal(
    selector: string,
    onAuthorize: OnAutorize,
  ): Promise<void> {
    const paymentInstance = await braintree.paypalCheckout.create({
      client: Braintree.client,
    });

    const element = document.querySelector(selector);
    // @ts-expect-error TODO: fix null safety
    element?.textContent = '';

    return window.paypal.Button.render({
      env: BRAINTREE_PAYPAL_ENV as any,
      commit: false,
      locale: 'en_US',
      style: style as any,
      onCancel: () => null,
      onError: () => null,
      payment: () => (
        paymentInstance.createPayment({
          flow: flow as any,
          enableShippingAddress: true,
        })
      ),
      onAuthorize: (data) => (
        paymentInstance.tokenizePayment(data)
          .then((payload) => {
            const address = (
              payload.details.shippingAddress || {}
            ) as PayPalAddress;

            const phone = (payload.details.phone || '').replace(/\D+/g, '');

            const recipientName = (
              address.recipientName
              || `${payload.details.firstName} ${payload.details.lastName}`
            ).split(' ');

            address.phone = phone;
            [address.firstName, address.lastName] = recipientName;

            onAuthorize(payload.nonce, new Address({
              address: address.line1,
              address2: address.line2 || '',
              city: address.city,
              first_name: address.firstName,
              last_name: address.lastName,
              local_phone_number: address.phone,
              delivery_instructions: '',
              primary: true,
              state: address.state,
              zip: address?.postalCode,
            }));

            return payload;
          })
      ),
      funding: {
        allowed: [window.paypal.FUNDING.CARD],
        disallowed: [window.paypal.FUNDING.CREDIT],
      },
    }, selector);
  }

  static async renderGooglePay(
    selector: string,
    onAuthorize: OnAutorize,
  ): Promise<void> {
    const paymentInstance = await braintree.googlePayment.create({
      client: Braintree.client,
      googlePayVersion: 2,
      googleMerchantId: BRAINTREE_GOOGLE_PAY_MERCHANT_ID,
    });

    const paymentsClient = new google.payments.api.PaymentsClient({
      environment: BRAINTREE_GOOGLE_PAY_ENV as GoogleEnv,
    });

    const paymentDataRequest = await paymentInstance
      .createPaymentDataRequest({
        transactionInfo: {
          currencyCode: 'USD',
          countryCode: 'US',
          totalPriceStatus: 'NOT_CURRENTLY_KNOWN',
        } as TransactionInfo,
      });

    const isReadyToPay = await paymentsClient.isReadyToPay({
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: paymentDataRequest.allowedPaymentMethods,
      existingPaymentMethodRequired: true,
    });

    if (isReadyToPay.result) {
      const element = document.querySelector(selector);

      // @ts-expect-error TODO: fix null safety
      element?.textContent = '';

      element?.appendChild(
        paymentsClient.createButton({
          buttonColor: 'black',
          buttonType: 'short',
          onClick: async (event) => {
            event.preventDefault();

            const ccMethod = paymentDataRequest.allowedPaymentMethods[0];
            ccMethod.parameters.billingAddressRequired = true;

            ccMethod.parameters.billingAddressParameters = {
              format: 'FULL',
              phoneNumberRequired: true,
            };

            ccMethod.parameters.allowedCardNetworks = [
              'VISA',
              'MASTERCARD',
              'DISCOVER',
            ];

            const paymentData = await paymentsClient.loadPaymentData(
              paymentDataRequest,
            );

            return paymentInstance.parseResponse(
              paymentData,
              (err, result) => {
                if (!err) {
                  const address = paymentData?.paymentMethodData?.info?.billingAddress;

                  const recipientName = address?.name?.split(' ');

                  onAuthorize(result.nonce, new Address({
                    address: address?.address1 || '',
                    address2: address?.address2 || '',
                    city: address?.locality || '',
                    first_name: recipientName ? recipientName[0] : '',
                    last_name: recipientName ? recipientName[1] : '',
                    local_phone_number: (
                      address?.phoneNumber?.match(/(\d+)/g) || []
                    ).join('')
                      .substr(1),
                    delivery_instructions: '',
                    primary: true,
                    state: address?.administrativeArea as keyof typeof State,
                    zip: address?.postalCode || '',
                  }));
                }
              },
            );
          },
        }),
      );
    }
  }

  static async triggerVenmo(onAuthorize: OnAutorize): Promise<void> {
    const results = await Promise.all([
      braintree.dataCollector.create({
        client: Braintree.client,
        paypal: true,
      }),
      braintree.venmo.create({ client: Braintree.client }),
    ]);

    const paymentInstance = results[1];

    if (paymentInstance.isBrowserSupported()) {
      paymentInstance.tokenize({}, (error, payload) => {
        if (!error) onAuthorize(payload?.nonce || '');
      });
    }
  }

  static async triggerApplePay(onAuthorize: OnAutorize): Promise<void> {
    const paymentInstance = await braintree.applePay.create({
      client: Braintree.client,
    });

    const paymentRequest = paymentInstance.createPaymentRequest({
      countryCode: 'US',
      currencyCode: 'USD',
      merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
      supportedNetworks: [
        'visa',
        'masterCard',
        'discover',
      ],
      total: {
        label: '$0 to subscribe with Apple Pay',
        type: 'pending',
        amount: '0',
      },
      requiredShippingContactFields: [
        'name',
        'postalAddress',
        'phone',
      ],
    } as ApplePayPaymentRequest);

    const session = new window.ApplePaySession(3, paymentRequest);
    session.begin();

    session.onvalidatemerchant = async (event) => {
      const merchantSession = await paymentInstance.performValidation({
        validationURL: event.validationURL,
        displayName: 'Purple Carrot',
      });

      await session.completeMerchantValidation(merchantSession);
    };

    session.onpaymentauthorized = async (event) => {
      const payload = await paymentInstance.tokenize({
        token: event.payment.token,
      });

      const address = event.payment.shippingContact;

      if (!address) {
        onAuthorize(payload.nonce);
      } else {
        onAuthorize(payload.nonce, new Address({
          address: address.addressLines[0],
          address2: address.addressLines[1] || '',
          city: address.locality,
          first_name: address.givenName,
          last_name: address.familyName,
          local_phone_number: (
            address.phoneNumber.match(/(\d+)/g) || []
          ).join(''),
          delivery_instructions: '',
          primary: true,
          state: address.administrativeArea,
          zip: address.postalCode,
        }));
      }

      session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
    };
  }
}

export type { CreditCard, OnToken };
export { PaymentService };
export default Braintree;
